import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import {
    Block,
    Check,
    Close,
    DeleteOutline,
    DescriptionOutlined,
    ErrorOutline,
    FileCopyOutlined,
    GetApp,
    HourglassEmpty,
    Print,
    ReceiptOutlined,
} from "@material-ui/icons"
import { useEffect, useState } from "react"
import { RegistrarLogErro } from "../../../services/clientefiel"
import { cancelarNotaFiscal, EmitirNotaFiscal } from "../../../services/integracao"
import {
    BaixarNotaFiscalPDF,
    emitirNotaFiscalConsumidor,
    sincronizarSituacaoNotaFiscal,
} from "../../../services/notasFiscais"

const ETAPA_VERIFICANDO = "verificando"
const ETAPA_SEM_NOTA = "sem-nota"
const ETAPA_EMITINDO = "emitindo"
const ETAPA_COM_NOTA = "com-nota"
const ETAPA_CANCELANDO = "cancelando"
const ETAPA_ERRO = "erro"

// A SEFAZ exige justificativa de cancelamento com 15 a 255 caracteres.
const MIN_JUSTIFICATIVA = 15
const MAX_JUSTIFICATIVA = 255

const VERDE = "#4caf50"
const VERDE_ESCURO = "#388e3c"
const VERMELHO = "#dc3545"

// Situação da NFC-e (status do PlugNotas) → faixa exibida no topo.
const SITUACOES = {
    CONCLUIDO: {
        titulo: "Nota fiscal emitida",
        descricao: "A NFC-e deste pedido foi autorizada. Escolha uma opção abaixo.",
        cor: "#1b7a34",
        fundo: "#e9f7ee",
        borda: "#c7ebd2",
        circulo: "#22a045",
        Icone: Check,
    },
    CANCELADO: {
        titulo: "Nota fiscal cancelada",
        descricao: "A NFC-e foi cancelada na SEFAZ. Se precisar, emita uma nova nota.",
        cor: "#424242",
        fundo: "#f3f4f6",
        borda: "#e5e7eb",
        circulo: "#6b7280",
        Icone: Block,
    },
    REJEITADO: {
        titulo: "Nota fiscal rejeitada",
        descricao: "A SEFAZ não autorizou a nota. Confira o motivo e emita novamente.",
        cor: "#b42318",
        fundo: "#fef3f2",
        borda: "#fecdca",
        circulo: "#d92d20",
        Icone: ErrorOutline,
    },
    DENEGADO: {
        titulo: "Nota fiscal denegada",
        descricao: "A SEFAZ denegou a nota. Confira o motivo abaixo.",
        cor: "#b42318",
        fundo: "#fef3f2",
        borda: "#fecdca",
        circulo: "#d92d20",
        Icone: ErrorOutline,
    },
    PROCESSANDO: {
        titulo: "Nota em processamento",
        descricao: "Aguardando o retorno da SEFAZ. Atualize a situação em instantes.",
        cor: "#93370d",
        fundo: "#fffaeb",
        borda: "#fedf89",
        circulo: "#f79009",
        Icone: HourglassEmpty,
    },
    SEM_NOTA: {
        titulo: "Nenhuma nota emitida",
        descricao: "Este pedido ainda não possui NFC-e.",
        cor: "#175cd3",
        fundo: "#eff8ff",
        borda: "#b2ddff",
        circulo: "#2e90fa",
        Icone: ReceiptOutlined,
    },
}

const situacaoDaNota = status => {
    const s = (status || "").toUpperCase()
    if (SITUACOES[s] && s !== "SEM_NOTA") return s
    // PENDENTE, GERANDO, SUBSTITUIDO etc.: ainda sem retorno definitivo da SEFAZ.
    return "PROCESSANDO"
}

const formatarCpfCnpj = valor => {
    const digitos = (valor || "").replace(/\D/g, "").slice(0, 14)
    if (digitos.length <= 11) {
        return digitos
            .replace(/(\d{3})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    }
    return digitos
        .replace(/^(\d{2})(\d)/, "$1.$2")
        .replace(/^(\d{2}\.\d{3})(\d)/, "$1.$2")
        .replace(/\.(\d{3})(\d)/, ".$1/$2")
        .replace(/(\d{4})(\d{1,2})$/, "$1-$2")
}

// Chave de acesso (44 dígitos) em blocos de 4, como no DANFE.
const formatarChave = chave => (chave || "").replace(/\D/g, "").replace(/(\d{4})(?=\d)/g, "$1 ")

// Chave NFC-e (44 díg.): cUF(2) AAMM(4) CNPJ(14) mod(2) série(3) nNF(9) ...
const serieDaChave = chave => {
    const d = (chave || "").replace(/\D/g, "")
    return d.length === 44 ? d.slice(22, 25) : null
}
const numeroDaNota = nota => {
    if (nota?.numero != null) return String(nota.numero).padStart(9, "0")
    const d = (nota?.chave || "").replace(/\D/g, "")
    return d.length === 44 ? d.slice(25, 34) : null
}

const formatarMoeda = valor =>
    valor == null
        ? null
        : Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor)

const paraData = valor => {
    if (!valor) return null
    const d = new Date(
        typeof valor === "string" && /^\d{4}-\d{2}-\d{2} /.test(valor) ? valor.replace(" ", "T") : valor
    )
    return Number.isNaN(d.getTime()) ? null : d
}
const formatarData = valor => {
    const d = paraData(valor)
    if (!d) return valor ? String(valor) : null
    return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
}
const formatarSoData = valor => {
    const d = paraData(valor)
    return d ? d.toLocaleDateString("pt-BR") : valor ? String(valor) : null
}
const formatarSoHora = valor => {
    const d = paraData(valor)
    return d ? d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : null
}

const useStyles = makeStyles({
    titulo: {
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "22px 24px 14px",
    },
    iconeTitulo: {
        width: 48,
        height: 48,
        borderRadius: 12,
        backgroundColor: "#e9f7ee",
        color: VERDE_ESCURO,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        "& svg": { fontSize: 28 },
    },
    tituloTexto: { fontWeight: 700, fontSize: "1.3rem", color: "#101828", lineHeight: 1.25 },
    subtitulo: { color: "#667085", fontSize: "0.85rem", marginTop: 2 },
    fechar: { marginLeft: "auto", alignSelf: "flex-start", color: "#667085" },
    conteudo: { padding: "4px 24px 8px" },
    faixa: {
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "16px 18px",
        borderRadius: 12,
        border: "1px solid",
        marginBottom: 16,
    },
    circulo: {
        width: 44,
        height: 44,
        borderRadius: "50%",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        "& svg": { fontSize: 26 },
    },
    faixaTitulo: { fontWeight: 700, fontSize: "1.05rem", lineHeight: 1.3 },
    faixaDescricao: { fontSize: "0.9rem", color: "#344054", marginTop: 2 },
    documento: {
        border: "1px solid #e4e7ec",
        borderRadius: 12,
        backgroundColor: "#fcfcfd",
        padding: "14px 18px 16px",
    },
    documentoTitulo: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontWeight: 700,
        color: "#101828",
        marginBottom: 12,
        "& svg": { color: "#667085" },
    },
    colunas: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        "& > div": { padding: "0 14px", borderLeft: "1px solid #e4e7ec" },
        "& > div:first-child": { paddingLeft: 0, borderLeft: "none" },
        "@media (max-width: 480px)": {
            gridTemplateColumns: "1fr",
            gap: 10,
            "& > div": { padding: 0, borderLeft: "none" },
        },
    },
    rotulo: {
        display: "block",
        fontSize: 13,
        color: "#667085",
        marginBottom: 4,
    },
    valorGrande: { fontSize: 17, fontWeight: 700, color: "#101828", letterSpacing: "0.3px" },
    valorPequeno: { fontSize: 12, color: "#667085" },
    extras: {
        display: "flex",
        flexWrap: "wrap",
        gap: "4px 20px",
        marginTop: 14,
        paddingTop: 12,
        borderTop: "1px dashed #e4e7ec",
        fontSize: 13,
        color: "#475467",
        "& strong": { color: "#101828", fontWeight: 600 },
    },
    chave: {
        display: "flex",
        alignItems: "center",
        gap: 4,
        fontFamily: "monospace",
        fontSize: 13,
        color: "#101828",
        backgroundColor: "white",
        border: "1px solid #e4e7ec",
        borderRadius: 8,
        padding: "4px 4px 4px 10px",
        marginTop: 10,
    },
    mensagem: {
        padding: "10px 12px",
        borderRadius: 8,
        fontSize: 13,
        marginTop: 12,
    },
    acoes: {
        padding: "16px 24px 22px",
        margin: "12px 0 0",
        borderTop: "1px solid #eaecf0",
        gap: 10,
        flexWrap: "wrap",
    },
    btnBase: {
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 8,
        padding: "9px 18px",
        fontSize: "0.95rem",
    },
    btnPrimario: {
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        boxShadow: "0 2px 8px rgba(var(--cor-botao-salvar-rgb),0.3)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
        "&.Mui-disabled": { backgroundColor: "#a5d6a7", color: "white" },
    },
    btnCinza: {
        backgroundColor: "#f2f4f7",
        color: "#344054",
        "&:hover": { backgroundColor: "#e4e7ec" },
    },
    btnContorno: {
        border: "1px solid #d0d5dd",
        color: "#344054",
        backgroundColor: "white",
        "&:hover": { backgroundColor: "#f9fafb" },
    },
    btnPerigo: {
        border: `1px solid ${VERMELHO}`,
        color: VERMELHO,
        backgroundColor: "white",
        "&:hover": { backgroundColor: "#fef3f2" },
    },
    btnPerigoCheio: {
        backgroundColor: VERMELHO,
        color: "white",
        "&:hover": { backgroundColor: "#b02a37" },
        "&.Mui-disabled": { backgroundColor: "#eeb3b9", color: "white" },
    },
    campoVerde: {
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: VERDE,
        },
        "& label.Mui-focused": { color: VERDE_ESCURO },
    },
})

const Coluna = ({ rotulo, valor, complemento }) => {
    const classes = useStyles()
    return (
        <Box>
            <span className={classes.rotulo}>{rotulo}:</span>
            <Typography component="div" className={classes.valorGrande}>
                {valor || "—"}
            </Typography>
            {complemento && <span className={classes.valorPequeno}>{complemento}</span>}
        </Box>
    )
}

export default function DialogEmissaoFiscal({ open, onClose, pedido, alertStart }) {
    const classes = useStyles()
    const [etapa, setEtapa] = useState(ETAPA_VERIFICANDO)
    const [nota, setNota] = useState(null)
    const [erroMsg, setErroMsg] = useState("")
    const [erroEmissao, setErroEmissao] = useState("")
    const [cpfCnpj, setCpfCnpj] = useState("")
    const [justificativa, setJustificativa] = useState("")
    const [confirmandoCancelar, setConfirmandoCancelar] = useState(false)
    const [confirmandoSaida, setConfirmandoSaida] = useState(false)
    // Nota rejeitada/cancelada: o usuário pediu para emitir de novo (mostra o formulário).
    const [reemitindo, setReemitindo] = useState(false)
    const [ocupado, setOcupado] = useState(false)

    const verificarSituacao = async () => {
        setEtapa(ETAPA_VERIFICANDO)
        setErroMsg("")
        try {
            const resp = await sincronizarSituacaoNotaFiscal(pedido.id)
            if (resp?.retornoErro) {
                setErroMsg(resp.mensagem || "Erro ao verificar a situação da nota")
                setEtapa(ETAPA_ERRO)
                return
            }
            setNota(resp.existe ? resp.data || {} : null)
            setEtapa(resp.existe ? ETAPA_COM_NOTA : ETAPA_SEM_NOTA)
        } catch (error) {
            RegistrarLogErro(error, "DialogEmissaoFiscal.verificarSituacao", {
                idPedido: pedido?.id,
            })
            setErroMsg(error?.message || "Erro inesperado")
            setEtapa(ETAPA_ERRO)
        }
    }

    useEffect(() => {
        if (open && pedido?.id) {
            setCpfCnpj("")
            setJustificativa("")
            setConfirmandoCancelar(false)
            setConfirmandoSaida(false)
            setReemitindo(false)
            setErroEmissao("")
            setNota(null)
            verificarSituacao()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, pedido?.id])

    const situacao = etapa === ETAPA_SEM_NOTA ? "SEM_NOTA" : situacaoDaNota(nota?.status)
    const exibirFormularioEmissao =
        etapa === ETAPA_SEM_NOTA ||
        (etapa === ETAPA_COM_NOTA && reemitindo && situacao !== "CONCLUIDO")

    const handleEmitir = async () => {
        const documento = cpfCnpj.replace(/\D/g, "")
        if (documento && documento.length !== 11 && documento.length !== 14) {
            alertStart?.("Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) completo.", "warning")
            return
        }
        setErroEmissao("")
        setEtapa(ETAPA_EMITINDO)
        const resp = await emitirNotaFiscalConsumidor(pedido.id, documento)
        if (resp?.retornoErro) {
            const motivo = resp.mensagem || "Falha ao emitir a nota fiscal"
            setErroEmissao(motivo)
            alertStart?.(motivo, "error")
            setEtapa(nota ? ETAPA_COM_NOTA : ETAPA_SEM_NOTA)
            return
        }
        alertStart?.("Nota fiscal enviada para emissão.", "success")
        setReemitindo(false)
        await verificarSituacao()
    }

    const handleBaixar = async () => {
        setOcupado(true)
        try {
            const response = await BaixarNotaFiscalPDF(pedido.id)
            try {
                const responseJSON = JSON.parse(await response.text())
                if (responseJSON.codErro) {
                    alertStart?.(responseJSON.mensagem, "warning")
                    return
                }
            } catch {
                // PDF normal — segue o fluxo
            }
            const blob = new Blob([response], { type: "application/pdf" })
            const objectURL = URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = objectURL
            link.download = `nfce-pedido-${pedido.id}.pdf`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(objectURL)
        } catch (error) {
            alertStart?.(error.message, "error")
            RegistrarLogErro(error, "DialogEmissaoFiscal.handleBaixar", { idPedido: pedido.id })
        } finally {
            setOcupado(false)
        }
    }

    const handleImprimir = async () => {
        setOcupado(true)
        const retorno = await EmitirNotaFiscal(pedido.id)
        setOcupado(false)
        if (retorno?.retornoErro) {
            alertStart?.(retorno.mensagem, "error")
            RegistrarLogErro(retorno, "DialogEmissaoFiscal.handleImprimir", { idPedido: pedido.id })
            return
        }
        alertStart?.("Nota fiscal enviada para impressão.", "info")
    }

    const handleCopiarChave = async () => {
        try {
            await navigator.clipboard.writeText((nota?.chave || "").replace(/\D/g, ""))
            alertStart?.("Chave de acesso copiada.", "success")
        } catch {
            alertStart?.("Não foi possível copiar a chave.", "warning")
        }
    }

    const justificativaValida = justificativa.trim().length >= MIN_JUSTIFICATIVA

    const handleConfirmarCancelamento = async () => {
        if (!justificativaValida) {
            alertStart?.(
                `A justificativa precisa ter pelo menos ${MIN_JUSTIFICATIVA} caracteres (exigência da SEFAZ).`,
                "warning"
            )
            return
        }
        setEtapa(ETAPA_CANCELANDO)
        const response = await cancelarNotaFiscal({
            id: pedido.id,
            justificativaCancelamentoNotaFiscal: justificativa.trim(),
        })
        alertStart?.(
            response?.mensagem ?? response?.message ?? "Solicitação de cancelamento enviada.",
            response?.codErro ? "error" : "info"
        )
        setConfirmandoCancelar(false)
        setJustificativa("")
        await verificarSituacao()
    }

    const bloqueado = etapa === ETAPA_EMITINDO || etapa === ETAPA_CANCELANDO

    const solicitarFechamento = () => {
        if (bloqueado) {
            setConfirmandoSaida(true)
            return
        }
        onClose?.()
    }

    const confirmarSaida = () => {
        setConfirmandoSaida(false)
        onClose?.()
    }

    const renderFaixa = () => {
        const { titulo, descricao, cor, fundo, borda, circulo, Icone } = SITUACOES[situacao]
        return (
            <Box className={classes.faixa} style={{ backgroundColor: fundo, borderColor: borda }}>
                <Box className={classes.circulo} style={{ backgroundColor: circulo }}>
                    <Icone />
                </Box>
                <Box>
                    <Typography className={classes.faixaTitulo} style={{ color: cor }}>
                        {titulo}
                    </Typography>
                    <Typography className={classes.faixaDescricao}>{descricao}</Typography>
                </Box>
            </Box>
        )
    }

    const renderProgresso = texto => (
        <Box display="flex" flexDirection="column" py={3} style={{ gap: 12 }}>
            <Typography variant="body2" align="center" style={{ color: "#374151" }}>
                {texto}
            </Typography>
            <LinearProgress style={{ borderRadius: 4 }} />
        </Box>
    )

    const renderFormularioEmissao = () => (
        <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
            <Typography variant="body2" style={{ color: "#374151" }}>
                {etapa === ETAPA_SEM_NOTA
                    ? "Emita a NFC-e deste pedido. Se o cliente pediu, informe o CPF ou CNPJ."
                    : "Uma nova NFC-e será emitida para este pedido."}
            </Typography>
            <TextField
                label="CPF ou CNPJ na nota (opcional)"
                variant="outlined"
                value={cpfCnpj}
                onChange={e => setCpfCnpj(formatarCpfCnpj(e.target.value))}
                onKeyDown={e => {
                    if (e.key === "Enter") handleEmitir()
                }}
                fullWidth
                size="small"
                placeholder="000.000.000-00"
                className={classes.campoVerde}
                autoFocus
            />
            {erroEmissao && (
                <Box
                    className={classes.mensagem}
                    style={{ backgroundColor: "#fdecea", color: "#611a15", marginTop: 0 }}
                >
                    <strong>Falha ao emitir:</strong> {erroEmissao}
                </Box>
            )}
        </Box>
    )

    const renderDetalhesNota = () => {
        const dataEmissao = nota?.dataAutorizacao || nota?.dataSolicitacao
        return (
            <>
                <Box className={classes.documento}>
                    <Typography component="div" className={classes.documentoTitulo}>
                        <DescriptionOutlined fontSize="small" />
                        Documento Fiscal
                    </Typography>
                    <Box className={classes.colunas}>
                        <Coluna rotulo="Número" valor={numeroDaNota(nota)} />
                        <Coluna rotulo="Série" valor={serieDaChave(nota?.chave)} />
                        <Coluna
                            rotulo="Data de emissão"
                            valor={formatarSoData(dataEmissao)}
                            complemento={formatarSoHora(dataEmissao)}
                        />
                    </Box>
                    {(nota?.valor != null || nota?.protocolo || nota?.dataCancelamento) && (
                        <Box className={classes.extras}>
                            {nota?.valor != null && (
                                <span>
                                    Valor: <strong>{formatarMoeda(nota.valor)}</strong>
                                </span>
                            )}
                            {nota?.protocolo && (
                                <span>
                                    Protocolo: <strong>{nota.protocolo}</strong>
                                </span>
                            )}
                            {nota?.dataCancelamento && (
                                <span>
                                    Cancelada em: <strong>{formatarData(nota.dataCancelamento)}</strong>
                                </span>
                            )}
                        </Box>
                    )}
                    {nota?.chave && (
                        <Box className={classes.chave}>
                            <Box flex={1}>{formatarChave(nota.chave)}</Box>
                            <Tooltip title="Copiar chave de acesso">
                                <IconButton size="small" onClick={handleCopiarChave}>
                                    <FileCopyOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
                {situacao !== "CONCLUIDO" && nota?.mensagem && (
                    <Box
                        className={classes.mensagem}
                        style={{
                            backgroundColor: SITUACOES[situacao].fundo,
                            color: SITUACOES[situacao].cor,
                        }}
                    >
                        <strong>Retorno da SEFAZ:</strong> {nota.mensagem}
                    </Box>
                )}
            </>
        )
    }

    const renderCancelamento = () => (
        <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
            <Box
                className={classes.mensagem}
                style={{ backgroundColor: "#fff4e0", color: "#8a5300", marginTop: 0 }}
            >
                O cancelamento é enviado à SEFAZ e não pode ser desfeito. O prazo para cancelar
                uma NFC-e é definido pela SEFAZ do seu estado.
            </Box>
            <TextField
                label="Justificativa do cancelamento"
                variant="outlined"
                value={justificativa}
                onChange={e => setJustificativa(e.target.value.slice(0, MAX_JUSTIFICATIVA))}
                fullWidth
                multiline
                minRows={3}
                autoFocus
                className={classes.campoVerde}
                helperText={
                    justificativaValida
                        ? `${justificativa.trim().length}/${MAX_JUSTIFICATIVA}`
                        : `Mínimo de ${MIN_JUSTIFICATIVA} caracteres (${justificativa.trim().length}/${MIN_JUSTIFICATIVA})`
                }
            />
        </Box>
    )

    const renderConteudo = () => {
        if (etapa === ETAPA_VERIFICANDO) {
            return (
                <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                    <CircularProgress style={{ color: VERDE }} />
                    <Typography variant="body2" style={{ marginTop: 12, color: "#6b7280" }}>
                        Consultando a situação da nota…
                    </Typography>
                </Box>
            )
        }
        if (etapa === ETAPA_EMITINDO) return renderProgresso("Emitindo a nota fiscal, aguarde…")
        if (etapa === ETAPA_CANCELANDO) return renderProgresso("Cancelando a nota fiscal, aguarde…")
        if (etapa === ETAPA_ERRO) {
            return (
                <Box
                    className={classes.mensagem}
                    style={{ backgroundColor: "#fdecea", color: "#611a15" }}
                >
                    {erroMsg}
                </Box>
            )
        }

        return (
            <>
                {renderFaixa()}
                {etapa === ETAPA_COM_NOTA && !reemitindo && !confirmandoCancelar && renderDetalhesNota()}
                {confirmandoCancelar && renderCancelamento()}
                {exibirFormularioEmissao && renderFormularioEmissao()}
            </>
        )
    }

    const renderAcoes = () => {
        if (etapa === ETAPA_VERIFICANDO) return null
        if (bloqueado) {
            return (
                <Button className={`${classes.btnBase} ${classes.btnContorno}`} onClick={solicitarFechamento}>
                    Fechar
                </Button>
            )
        }
        if (etapa === ETAPA_ERRO) {
            return (
                <>
                    <Button className={`${classes.btnBase} ${classes.btnContorno}`} onClick={onClose}>
                        Fechar
                    </Button>
                    <Button className={`${classes.btnBase} ${classes.btnPrimario}`} onClick={verificarSituacao}>
                        Tentar novamente
                    </Button>
                </>
            )
        }
        if (confirmandoCancelar) {
            return (
                <>
                    <Button
                        className={`${classes.btnBase} ${classes.btnContorno}`}
                        onClick={() => {
                            setConfirmandoCancelar(false)
                            setJustificativa("")
                        }}
                    >
                        Voltar
                    </Button>
                    <Button
                        className={`${classes.btnBase} ${classes.btnPerigoCheio}`}
                        onClick={handleConfirmarCancelamento}
                        disabled={!justificativaValida}
                    >
                        Confirmar cancelamento
                    </Button>
                </>
            )
        }
        if (exibirFormularioEmissao) {
            return (
                <>
                    <Button
                        className={`${classes.btnBase} ${classes.btnContorno}`}
                        onClick={() => (reemitindo ? setReemitindo(false) : onClose())}
                    >
                        {reemitindo ? "Voltar" : "Fechar"}
                    </Button>
                    <Button
                        className={`${classes.btnBase} ${classes.btnPrimario}`}
                        onClick={handleEmitir}
                        startIcon={<ReceiptOutlined />}
                    >
                        Emitir NFC-e
                    </Button>
                </>
            )
        }
        if (situacao === "CONCLUIDO") {
            return (
                <Box display="flex" width="100%" alignItems="center" flexWrap="wrap" style={{ gap: 10 }}>
                    <Button
                        className={`${classes.btnBase} ${classes.btnContorno}`}
                        onClick={onClose}
                    >
                        Fechar
                    </Button>
                    <Box flex={1} />
                    <Button
                        className={`${classes.btnBase} ${classes.btnPerigo}`}
                        onClick={() => setConfirmandoCancelar(true)}
                        startIcon={<DeleteOutline />}
                    >
                        Cancelar nota
                    </Button>
                    <Button
                        className={`${classes.btnBase} ${classes.btnCinza}`}
                        onClick={handleImprimir}
                        disabled={ocupado}
                        startIcon={<Print />}
                    >
                        Imprimir
                    </Button>
                    <Button
                        className={`${classes.btnBase} ${classes.btnPrimario}`}
                        onClick={handleBaixar}
                        disabled={ocupado}
                        startIcon={<GetApp />}
                    >
                        Baixar PDF
                    </Button>
                </Box>
            )
        }
        if (situacao === "PROCESSANDO") {
            return (
                <>
                    <Button className={`${classes.btnBase} ${classes.btnContorno}`} onClick={onClose}>
                        Fechar
                    </Button>
                    <Button className={`${classes.btnBase} ${classes.btnPrimario}`} onClick={verificarSituacao}>
                        Atualizar situação
                    </Button>
                </>
            )
        }
        // Rejeitada, denegada ou cancelada: pode emitir uma nova nota.
        return (
            <>
                <Button className={`${classes.btnBase} ${classes.btnContorno}`} onClick={onClose}>
                    Fechar
                </Button>
                <Button
                    className={`${classes.btnBase} ${classes.btnPrimario}`}
                    onClick={() => {
                        setErroEmissao("")
                        setReemitindo(true)
                    }}
                    startIcon={<ReceiptOutlined />}
                >
                    Emitir nova nota
                </Button>
            </>
        )
    }

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason === "backdropClick") return
                solicitarFechamento()
            }}
            onClick={e => e.stopPropagation()}
            maxWidth="sm"
            fullWidth
            PaperProps={{ style: { borderRadius: 16 } }}
        >
            <DialogTitle disableTypography className={classes.titulo}>
                <Box className={classes.iconeTitulo}>
                    <ReceiptOutlined />
                </Box>
                <Box>
                    <Typography className={classes.tituloTexto}>Nota Fiscal Consumidor</Typography>
                    <Typography className={classes.subtitulo}>
                        NFC-e{pedido?.id ? ` · Pedido #${pedido.id}` : ""}
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={solicitarFechamento}
                    aria-label="Fechar"
                    className={classes.fechar}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.conteudo}>{renderConteudo()}</DialogContent>
            <DialogActions className={classes.acoes}>{renderAcoes()}</DialogActions>
            <Dialog
                open={confirmandoSaida}
                onClose={() => setConfirmandoSaida(false)}
                onClick={e => e.stopPropagation()}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Sair da emissão?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        A operação com a nota fiscal está em andamento. Você pode voltar aqui quando
                        quiser para conferir a situação. Deseja realmente sair?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button className={`${classes.btnBase} ${classes.btnContorno}`} onClick={() => setConfirmandoSaida(false)}>
                        Não
                    </Button>
                    <Button className={`${classes.btnBase} ${classes.btnPrimario}`} onClick={confirmarSaida}>
                        Sim, sair
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    )
}
